// src/components/Colegiado/EventosColegiado.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import axios from "axios";
import {
  Box, Paper, Typography, Button, IconButton, Grid, Stack,
  Snackbar, Alert, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, TablePagination, Chip, Tooltip, Divider, Select, MenuItem,
  CircularProgress
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CancelIcon from "@mui/icons-material/Cancel";
import dayjs from "dayjs";
import "dayjs/locale/es";

dayjs.locale("es");

const API = process.env.REACT_APP_API_URL;

const headersAuth = () => {
  const token = localStorage.getItem("token");
  return { Authorization: token ? `Bearer ${token}` : undefined };
};

// Formatea ISO "YYYY-MM-DD" -> "DD/MM/YYYY"
const fmtES = (iso) => {
  if (!iso) return "-";
  const d = dayjs(iso);
  return d.isValid() ? d.format("DD/MM/YYYY") : iso;
};

// Obtiene la descripción desde posibles claves
const getDescripcion = (row) => row?.descripcion ?? row?.description ?? "";

export default function EventosColegiado() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);
  const [sort, setSort] = useState("fecha,asc");
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });


  // Palabras clave de eventos "simples" (solo fecha)
  const SIMPLE_KEYWORDS = ["charla", "reunion", "meeting", "ponencia", "curso", "coloquio", "Autobús", "autobús", "autobus", "bus"];

  // Normaliza para ignorar acentos: "Reunión" -> "reunion"
  const stripAccents = (s) =>
    s ? s.normalize("NFD").replace(/[\u0300-\u036f]/g, "") : s;

  const isSimpleEvent = (nombre) => {
    if (!nombre) return false;
    const n = stripAccents(String(nombre).toLowerCase());
    return SIMPLE_KEYWORDS.some((k) => n.includes(k));
  };

  // Panel inline
  const [editingId, setEditingId] = useState(null); // id evento abierto
  const [miIns, setMiIns] = useState(null); // {dia, familia, otro, entradas, existe}

  // Mapa: eventoId -> bool (si estoy inscrito)
  const [inscritoMap, setInscritoMap] = useState({});

  const api = useMemo(() => axios.create({ baseURL: API, withCredentials: false }), []);
  const handleSnack = (msg, sev = "success") => setSnack({ open: true, msg, sev });

  /* ==========================
     BLOQUEO por estado/recibos
  ========================== */
  const [miEstadoId, setMiEstadoId] = useState(null);
  const [tienePendientes, setTienePendientes] = useState(false);
  const [checkingCondicion, setCheckingCondicion] = useState(false);
  const [miCodigo, setMiCodigo] = useState(null);

  const pendientesReq = useRef(0);

  const extractCodigo = (c) => {
    const v = c?.codigo ?? c?.codigo_colegiado ?? c?.codigoColegiado ?? c?.colegiadoCodigo ?? c?.id;
    const n = v != null ? Number(v) : null;
    return Number.isFinite(n) ? n : null;
  };

  const extractEstadoId = (c) => {
    const v = c?.estadoId ?? c?.estado_id ?? c?.estado?.id ?? c?.estado;
    const n = v != null ? Number(v) : null;
    return Number.isFinite(n) ? n : null;
  };

  const bloqueadoPorCondicion =
    (miEstadoId != null && Number(miEstadoId) > 6) || !!tienePendientes;

  const fetchMiPerfil = useCallback(async () => {
    const headers = headersAuth();
    const { data } = await api.get("/colegiado/perfil", { headers });
    return data || null;
  }, [api]);

  const fetchPendientesForCodigo = useCallback(async (codigo) => {
    const headers = headersAuth();
    const myReq = ++pendientesReq.current;

    if (codigo == null) {
      if (myReq === pendientesReq.current) setTienePendientes(false);
      return;
    }

    // 1) BULK
    try {
      const body = { codigos: [codigo], estados: ["PENDIENTE", "PENDIENTE_DEVUELTO"] };
      const { data } = await api.post("/api/recibos/pending/map", body, { headers });
      if (data && typeof data === "object") {
        const v = data[codigo] ?? data[String(codigo)];
        const has = v === true || v === "true" || (typeof v === "number" && v > 0);
        if (myReq === pendientesReq.current) setTienePendientes(!!has);
        return;
      }
    } catch {
      // fallback
    }

    // 2) fallback por /api/recibos (2 llamadas size mínimo)
    try {
      const [r1, r2] = await Promise.all([
        api.get("/api/recibos", {
          headers,
          params: { colegiado: codigo, resultadoBanco: "PENDIENTE", page: 0, size: 1 },
        }),
        api.get("/api/recibos", {
          headers,
          params: { colegiado: codigo, resultadoBanco: "PENDIENTE_DEVUELTO", page: 0, size: 1 },
        }),
      ]);

      const hasAny = (res) => {
        const d = res?.data;
        if (Array.isArray(d)) return d.length > 0;
        if (d && typeof d.totalElements === "number") return d.totalElements > 0;
        if (d && typeof d.total_elements === "number") return d.total_elements > 0;
        return false;
      };

      if (myReq === pendientesReq.current) setTienePendientes(hasAny(r1) || hasAny(r2));
    } catch {
      if (myReq === pendientesReq.current) setTienePendientes(false);
    }
  }, [api]);

  const recargarCondicion = useCallback(async () => {
    try {
      setCheckingCondicion(true);

      const perfil = await fetchMiPerfil();
      if (!perfil) {
        setMiCodigo(null);
        setMiEstadoId(null);
        setTienePendientes(false);
        return;
      }

      const codigo = extractCodigo(perfil);
      const estadoId = extractEstadoId(perfil);

      setMiCodigo(codigo);
      setMiEstadoId(estadoId);

      await fetchPendientesForCodigo(codigo);
    } finally {
      setCheckingCondicion(false);
    }
  }, [fetchMiPerfil, fetchPendientesForCodigo]);

  useEffect(() => {
    recargarCondicion();
  }, [recargarCondicion]);

  // Si se bloquea con el panel abierto, lo cerramos
  useEffect(() => {
    if (bloqueadoPorCondicion && editingId != null) {
      setEditingId(null);
      setMiIns(null);
    }
  }, [bloqueadoPorCondicion, editingId]);

  // Si se vuelve bloqueado mientras está el panel abierto -> lo cerramos
  useEffect(() => {
    if (bloqueadoPorCondicion && editingId != null) {
      setEditingId(null);
      setMiIns(null);
    }
  }, [bloqueadoPorCondicion, editingId]);

  /* ==========================
     Carga de eventos
  ========================== */

  useEffect(() => { fetchEventos(); /* eslint-disable-next-line */ }, [page, size, sort]);

  async function fetchEventos() {
    try {
      setLoading(true);
      const params = { page, size, sort };
      const { data } = await api.get("/api/eventos/page", { params, headers: headersAuth() });
      const list = data.content || [];
      setRows(list);
      setTotal(data.totalElements ?? data.total_elements ?? data.total ?? 0);
      await preloadMisInscripciones(list);
    } catch {
      handleSnack("Error al cargar eventos", "error");
    } finally {
      setLoading(false);
    }
  }

  async function preloadMisInscripciones(list) {
    try {
      const checks = await Promise.allSettled(
        (list || []).map(ev =>
          api.get(`/api/eventos/${ev.id}/mi-inscripcion`, { headers: headersAuth() })
        )
      );
      const result = {};
      checks.forEach((res, idx) => {
        const ev = list[idx];
        result[ev.id] = res.status === "fulfilled"; // existe inscripción -> true
      });
      setInscritoMap(result);
    } catch {
      // no bloquear UI
    }
  }

  async function cargarMiInscripcion(idEvento) {
    try {
      const { data } = await api.get(`/api/eventos/${idEvento}/mi-inscripcion`, { headers: headersAuth() });
      setMiIns({
        id_evento: data.id_evento,
        dia: data.dia || "", // "YYYY-MM-DD"
        familia: data.familia ?? "NO",
        otro: data.otro ?? "NO",
        entradas: data.entradas != null ? String(data.entradas) : "2",
        existe: true,
      });
    } catch {
      setMiIns({
        id_evento: idEvento,
        dia: "",
        familia: "NO",
        otro: "NO",
        entradas: "2",
        existe: false,
      });
    }
  }

  async function abrirPanel(row) {
    if (bloqueadoPorCondicion) {
      handleSnack("Debido a su condición actual no es posible inscribirse en ningún evento", "warning");
      return;
    }
    setEditingId(row.id);
    await cargarMiInscripcion(row.id);
  }

  function cerrarPanel() {
    setEditingId(null);
    setMiIns(null);
  }

  // Rango de entradas según familia numerosa y si es simple
  const getEntradasOptions = (simpleMode, familia) => {
    if (simpleMode) return []; // simple -> no entradas
    return (familia === "SI") ? [2, 3, 4, 5] : [2, 3];
  };

  // Ajuste de entradas al cambiar familia numerosa
  useEffect(() => {
    if (!miIns || !editingId) return;
    const row = rows.find(r => r.id === editingId);
    const simpleMode = isSimpleEvent(row?.nombre);
    const opciones = getEntradasOptions(simpleMode, miIns.familia);
    if (opciones.length === 0) return;

    const actual = Number(miIns.entradas);
    if (!opciones.includes(actual)) {
      setMiIns(s => ({ ...s, entradas: String(opciones[0]) }));
    }
  }, [miIns?.familia, editingId, rows]);

  async function inscribirme() {
    if (!editingId || !miIns) return;

    if (bloqueadoPorCondicion) {
      handleSnack("Debido a su condición actual no es posible inscribirse en ningún evento", "warning");
      return;
    }

    try {
      const row = rows.find(r => r.id === editingId);
      const simpleMode = isSimpleEvent(row?.nombre);

      // Validaciones
      if (!miIns.dia) {
        handleSnack("Selecciona una fecha", "warning");
        return;
      }
      if (!simpleMode) {
        const opciones = getEntradasOptions(false, miIns.familia);
        const entradasNum = Number(miIns.entradas);
        if (!opciones.includes(entradasNum)) {
          handleSnack("Número de entradas no permitido para la opción seleccionada", "warning");
          return;
        }
      }

      await api.post(`/api/eventos/${editingId}/inscribirme`, null, {
        headers: headersAuth(),
        params: simpleMode
          ? { dia: miIns.dia || undefined }
          : {
            dia: miIns.dia || undefined,
            familia: miIns.familia || undefined,
            entradas: miIns.entradas ? Number(miIns.entradas) : undefined,
            otro: miIns.otro || undefined,
          },
      });

      handleSnack("Inscripción realizada");
      cerrarPanel();
      fetchEventos();
      // por si tras inscribirse quieres volver a comprobar (opcional)
      // recargarCondicion();
    } catch (e) {
      handleSnack(e?.response?.data?.message || "No se pudo inscribir", "error");
    }
  }

  async function cancelarMiInscripcion() {
    if (!editingId) return;
    if (!window.confirm("¿Seguro que deseas cancelar tu inscripción?")) return;
    try {
      await api.delete(`/api/eventos/${editingId}/mi-inscripcion`, { headers: headersAuth() });
      handleSnack("Inscripción cancelada");
      cerrarPanel();
      fetchEventos();
    } catch {
      handleSnack("No se pudo cancelar", "error");
    }
  }

  const estadoChip = (estado) => {
    const color = estado === "CERRADO" ? "error" : estado === "ABIERTO" ? "success" : "default";
    return <Chip size="small" label={estado || "-"} color={color} variant="outlined" />;
  };

  const renderPanel = () => {
    if (!editingId || !miIns) return null;
    const row = rows.find(r => r.id === editingId);
    if (!row) return null;

    const simpleMode = isSimpleEvent(row?.nombre);
    const cerrado = row.estado === "CERRADO";
    const fechasDisponibles = (row.fechas || []).slice().sort();
    const sinFechas = fechasDisponibles.length === 0;

    const entradasOpciones = getEntradasOptions(simpleMode, miIns.familia);
    const entradasValida = simpleMode || entradasOpciones.includes(Number(miIns.entradas));

    // además de lo de siempre, aplicamos el bloqueo global
    const puedeInscribirse =
      !bloqueadoPorCondicion &&
      !miIns.existe &&
      !cerrado &&
      !sinFechas &&
      !!miIns.dia &&
      entradasValida;

    const descripcion = getDescripcion(row);

    return (
      <Paper elevation={0} sx={{ p: 2, border: "1px dashed #ccc", borderRadius: 2, mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {miIns.existe ? "Mi inscripción" : "Inscribirme"}
          </Typography>
          <Typography variant="body2" color="text.secondary">({row?.nombre})</Typography>
          {miIns.existe && <Chip size="small" color="info" label="YA INSCRITO" sx={{ ml: 1 }} />}
          {cerrado && <Chip size="small" color="error" label="CERRADO" sx={{ ml: 1 }} />}
          {bloqueadoPorCondicion && <Chip size="small" color="warning" label="BLOQUEADO" sx={{ ml: 1 }} />}
        </Stack>

        {bloqueadoPorCondicion && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Debido a su condición actual no es posible inscribirse en ningún evento.
          </Alert>
        )}

        {/* Descripción */}
        {descripcion && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Descripción
            </Typography>
            {
              /<[^>]+>/.test(descripcion) ? (
                <Paper
                  variant="outlined"
                  sx={{ p: 1.5, borderRadius: 2, maxHeight: 240, overflow: "auto" }}
                  dangerouslySetInnerHTML={{ __html: descripcion }}
                />
              ) : (
                <Paper
                  variant="outlined"
                  sx={{ p: 1.5, borderRadius: 2, whiteSpace: "pre-wrap", maxHeight: 240, overflow: "auto" }}
                >
                  {descripcion}
                </Paper>
              )
            }
          </Box>
        )}

        <Grid container spacing={2}>
          {/* FECHA: siempre visible */}
          <Grid item xs={12} md={3}>
            <Typography variant="caption">Fecha</Typography>
            <Select
              fullWidth
              value={miIns.dia}
              onChange={(e) => setMiIns(s => ({ ...s, dia: e.target.value }))}
              disabled={miIns.existe || sinFechas || cerrado || bloqueadoPorCondicion}
              displayEmpty
            >
              <MenuItem value="" disabled>Selecciona una fecha</MenuItem>
              {fechasDisponibles.map(f => (
                <MenuItem key={f} value={f}>{fmtES(f)}</MenuItem>
              ))}
            </Select>
            {sinFechas && (
              <Typography variant="caption" color="error">Este evento no tiene fechas disponibles</Typography>
            )}
          </Grid>

          {/* Controles solo si NO es simple */}
          {!simpleMode && (
            <>
              <Grid item xs={12} md={3}>
                <Typography variant="caption">¿Otro día?</Typography>
                <Select
                  fullWidth
                  value={miIns.otro}
                  onChange={(e) => setMiIns(s => ({ ...s, otro: e.target.value }))}
                  disabled={miIns.existe || cerrado || bloqueadoPorCondicion}
                >
                  <MenuItem value="SI">SI</MenuItem>
                  <MenuItem value="NO">NO</MenuItem>
                </Select>
              </Grid>

              <Grid item xs={12} md={3}>
                <Typography variant="caption">Familia Numerosa</Typography>
                <Select
                  fullWidth
                  value={miIns.familia}
                  onChange={(e) => setMiIns(s => ({ ...s, familia: e.target.value }))}
                  disabled={miIns.existe || cerrado || bloqueadoPorCondicion}
                >
                  <MenuItem value="SI">SI</MenuItem>
                  <MenuItem value="NO">NO</MenuItem>
                </Select>
              </Grid>

              <Grid item xs={12} md={3}>
                <Typography variant="caption">Entradas</Typography>
                <Select
                  fullWidth
                  value={miIns.entradas}
                  onChange={(e) => setMiIns(s => ({ ...s, entradas: e.target.value }))}
                  disabled={miIns.existe || cerrado || bloqueadoPorCondicion}
                >
                  {entradasOpciones.map(n => (
                    <MenuItem key={n} value={String(n)}>{n}</MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary">
                  {miIns.familia === "SI" ? "Permitidas: 2 a 5" : "Permitidas: 2 a 3"}
                </Typography>
              </Grid>
            </>
          )}
        </Grid>

        <Stack direction="row" spacing={1} sx={{ mt: 2 }} justifyContent="flex-end">
          <Button onClick={cerrarPanel}>Cerrar</Button>

          {miIns.existe ? (
            <Button color="error" startIcon={<CancelIcon />} onClick={cancelarMiInscripcion}>
              Cancelar inscripción
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<EventAvailableIcon />}
              onClick={inscribirme}
              disabled={!puedeInscribirse}
            >
              Inscribirme
            </Button>
          )}
        </Stack>

        {!miIns.existe && row.estado === "CERRADO" && (
          <Typography variant="caption" color="text.secondary">
            El evento está cerrado: no es posible inscribirse.
          </Typography>
        )}
      </Paper>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Paper elevation={3} sx={{ p: 2, borderRadius: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Eventos</Typography>

          <Tooltip title="Refrescar">
            <span>
              <IconButton onClick={fetchEventos} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Box sx={{ flex: 1 }} />

          <Tooltip title="Revisar mi condición (estado/recibos)">
            <span>
              <Button
                size="small"
                variant="outlined"
                onClick={recargarCondicion}
                disabled={checkingCondicion}
              >
                {checkingCondicion ? "Comprobando..." : "Comprobar"}
              </Button>
            </span>
          </Tooltip>
        </Stack>

        {/* Letrero global de bloqueo */}
        {(checkingCondicion) && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Comprobando tu estado y recibos pendientes...
          </Alert>
        )}

        {bloqueadoPorCondicion && !checkingCondicion && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Debido a su condición actual no es posible inscribirse en ningún evento.
          </Alert>
        )}

        <Divider sx={{ mb: 2 }} />

        {/* Tabla de eventos */}
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Fecha (mín.)</TableCell>
                <TableCell>Fechas</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Max</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const cerrado = row.estado === "CERRADO";
                const abierto = editingId === row.id;
                const estoyInscrito = !!inscritoMap[row.id];

                const disabledAccion = cerrado || bloqueadoPorCondicion || checkingCondicion;

                let boton = null;
                if (!cerrado) {
                  boton = (
                    <Tooltip
                      title={
                        bloqueadoPorCondicion
                          ? "No puedes inscribirte por tu condición actual"
                          : (estoyInscrito ? "Ver mi inscripción" : "Inscribirme")
                      }
                    >
                      <span>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={estoyInscrito ? <VisibilityIcon /> : <EventAvailableIcon />}
                          onClick={() => abrirPanel(row)}
                          disabled={disabledAccion}
                        >
                          {estoyInscrito ? "Ver" : "Inscribirse"}
                        </Button>
                      </span>
                    </Tooltip>
                  );
                }

                return (
                  <React.Fragment key={row.id}>
                    <TableRow hover>
                      <TableCell>{row.nombre}</TableCell>
                      <TableCell>{fmtES(row.fecha)}</TableCell>
                      <TableCell>
                        {(row.fechas?.length ?? 0) > 0 ? (
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {(row.fechas || []).slice(0, 3).map((f) => (
                              <Chip key={f} size="small" label={fmtES(f)} />
                            ))}
                            {(row.fechas?.length || 0) > 3 && (
                              <Chip size="small" variant="outlined" label={`+${row.fechas.length - 3}`} />
                            )}
                          </Stack>
                        ) : "-"}
                      </TableCell>
                      <TableCell>{estadoChip(row.estado)}</TableCell>
                      <TableCell>{row.tope ?? "-"}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          {boton}
                        </Stack>
                      </TableCell>
                    </TableRow>
                    {abierto && (
                      <TableRow>
                        <TableCell colSpan={7}>{renderPanel()}</TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}

              {rows.length === 0 && !loading && (
                <TableRow><TableCell colSpan={7} align="center">Sin resultados</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          rowsPerPage={size}
          onRowsPerPageChange={(e) => { setSize(parseInt(e.target.value, 10)); setPage(0); }}
          labelRowsPerPage="Filas por página"
        />
      </Paper>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
      >
        <Alert severity={snack.sev} variant="filled" onClose={() => setSnack(s => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
